import { DataLayerMessage, MessageType } from '../types';
import debugLogger from '../utils/DebugLogger';

class DataLayerService {
  private listeners: Map<MessageType, Function[]> = new Map();
  private parentOrigin: string = '*';

  constructor() {
    this.initMessageListener();
    debugLogger.log('DataLayerService initialized');
  }

  // Initialize message listener for parent window communication
  private initMessageListener() {
    window.addEventListener('message', (event) => {
      try {
        if (event.source !== window.parent || !event.data || typeof event.data !== 'object') {
          return;
        }
        const message: DataLayerMessage = event.data;

        if (this.parentOrigin === '*') {
          this.parentOrigin = event.origin;
        } else if (event.origin !== this.parentOrigin) {
          debugLogger.warn('Message from unauthorized origin', { origin: event.origin, expected: this.parentOrigin });
          return;
        }

        if (message.type) {
          debugLogger.info(`📨 Received: ${message.type}`, message);
          this.handleMessage(message);
        }
      } catch (error) {
        debugLogger.error('Error processing message', error);
      }
    });
  }

  // Set parent origin for security
  setParentOrigin(origin: string) {
    this.parentOrigin = origin;
  }

  // Handle incoming messages
  private handleMessage(message: DataLayerMessage) {
    const listeners = this.listeners.get(message.type as MessageType);
    if (listeners) {
      listeners.forEach(listener => {
        try {
          listener(message);
        } catch (error) {
          // Error in message listener
        }
      });
    }
  }

  // Subscribe to specific message types
  subscribe(messageType: MessageType, callback: (message: DataLayerMessage) => void) {
    if (!this.listeners.has(messageType)) {
      this.listeners.set(messageType, []);
    }
    this.listeners.get(messageType)!.push(callback);

    // Return unsubscribe function
    return () => {
      const listeners = this.listeners.get(messageType);
      if (listeners) {
        const index = listeners.indexOf(callback);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      }
    };
  }

  // Send message to parent window
  sendToParent(message: DataLayerMessage) {
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({
          ...message,
          source: 'fashion-connect-widget',
          timestamp: Date.now()
        }, this.parentOrigin);
        debugLogger.info(`📤 Sent: ${message.type}`, message);
      } else {
        debugLogger.warn('Cannot send to parent: No parent window found');
      }
    } catch (error) {
      debugLogger.error('Error sending message to parent', error);
    }
  }

  // Notify parent that widget is ready
  notifyReady() {
    this.sendToParent({
      type: 'widget-ready',
      payload: {
        version: '1.0.0',
        capabilities: [
          'basket-combin',
          'product-detail-combin',
          'category-combin',
          'style-advice',
          'size-guide',
          'fash-size',
          'color-match'
        ]
      }
    });
  }

  // Request data from parent
  requestData(dataType: string, params?: any) {
    this.sendToParent({
      type: 'data-request',
      payload: {
        dataType,
        params
      }
    });
  }

  // Notify parent of widget close
  notifyClose() {
    this.sendToParent({
      type: 'widget-close'
    });
  }
}

const dataLayerService = new DataLayerService();
export default dataLayerService;
